import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Space, Typography, Row, Col, Pagination, Tag, Tooltip, Badge, Dropdown, Image, Button, message, Divider } from 'antd';
import { SyncOutlined, DatabaseOutlined, CaretDownFilled, ThunderboltOutlined, BoxPlotOutlined, CarOutlined, BgColorsOutlined, CheckCircleOutlined, WarningOutlined, SendOutlined, UploadOutlined, EditOutlined, ExclamationCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { TruckIcon, DeliveredIcon } from '../../../utils/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import EditableCell from './EditableCell';
import EditableCellWithNoteTemplates from '../../Orders/components/EditableCellWithNoteTemplates';
import SyncShopifyOrdersModal from './SyncShopifyOrdersModal';
import UploadShippingCsvModal from './UploadShippingCsvModal';
import ShippingInfoModal from './ShippingInfoModal';
import ShopifyCustomizationModal from './ShopifyCustomizationModal';
import ShopifyConfirmShipmentModal from '../modals/ShopifyConfirmShipmentModal';
import ShopifySendMockupModal from '../modals/ShopifySendMockupModal';
import SendCustomMessageModal from '../modals/SendCustomMessageModal';
import StatusOverview from './StatusOverview';
import StatusOverviewTooltip from './StatusOverviewTooltip';
import { ShippingTimelineModal, FulfillToPrinterModal, BulkEditModal } from '../../Orders/modals';
import { usePrinterOptions, useCategoryOptions, useUserOptions } from '../hooks/useSelectOptions';
import { createCellSaveHandler } from '../utils/cellSaveHandler';
import { fetchOrderItems, syncShopifyOrders, confirmShopifyShipment } from '../actions';
import { getDataCommon } from '../../../redux/slices/dataCommonSlice';
import { MemoizedOrderStatus, MemoizedCustomizationStatus } from '../../../utils/statusComponents';
import OrderService from '../../../services/orderService';
import { orderPermissions, shopifyOrderPermissions } from '../../../utils/permissions';
import api from '../../../services/api';
const { Text, Paragraph } = Typography;

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const ShopifyOrdersList = ({
  orderItems,
  isLoading,
  pagination,
  onPaginationChange,
  user,
  filters
}) => {
  const dispatch = useDispatch();
  const { printers, shopifyCategories, users, shopifyStores } = useSelector(state => state.dataCommon);
  const { syncLoading } = useSelector(state => state.shopifyOrders);

  const normalizeTrelloUrl = useCallback((trelloCardIdOrUrl) => {
    if (!trelloCardIdOrUrl) return null;
    if (typeof trelloCardIdOrUrl !== 'string') return null;

    const trimmed = trelloCardIdOrUrl.trim();
    if (!trimmed) return null;

    // If backend already provided a URL, keep it.
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    // Common Trello shortLink format: https://trello.com/c/<shortLink>
    return `https://trello.com/c/${trimmed}`;
  }, []);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [uploadCsvModalVisible, setUploadCsvModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCustomizationModalVisible, setIsCustomizationModalVisible] = useState(false);
  const [selectedCustomization, setSelectedCustomization] = useState(null);
  const [isShippingTimelineModalVisible, setIsShippingTimelineModalVisible] = useState(false);
  const [selectedTrackingInfo, setSelectedTrackingInfo] = useState(null);
  const [isFulfillToPrinterModalVisible, setIsFulfillToPrinterModalVisible] = useState(false);
  const [selectedOrdersForFulfill, setSelectedOrdersForFulfill] = useState([]);
  const [refreshingTrackerIds, setRefreshingTrackerIds] = useState(new Set());
  const [syncingFulfillmentIds, setSyncingFulfillmentIds] = useState(new Set());
  const [isConfirmShipmentModalVisible, setIsConfirmShipmentModalVisible] = useState(false);
  const [shipmentReadyOrders, setShipmentReadyOrders] = useState([]);
  const [isSendMockupModalVisible, setIsSendMockupModalVisible] = useState(false);
  const [mockupReadyOrders, setMockupReadyOrders] = useState([]);
  const [isSendCustomMessageModalVisible, setIsSendCustomMessageModalVisible] = useState(false);
  const [selectedOrdersForCustomMessage, setSelectedOrdersForCustomMessage] = useState([]);
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);

  const canFulfillToPrinter = orderPermissions.canFulfillToPrinter(user);
  const canBulkEdit = orderPermissions.canBulkEdit(user);
  const canExportCSV = orderPermissions.canExportCSV(user);
  const canSyncAmazon = orderPermissions.canSyncAmazon(user);
  const canUpdateOrders = orderPermissions.canUpdate(user);
  const canConfirmShipment = orderPermissions.canConfirmShipment(user);
  const canSendMockup = orderPermissions.canSendMockup(user);
  const canSendCustomMessage = shopifyOrderPermissions.canSendBulkMessage(user);
  const canViewSensitiveInfo = shopifyOrderPermissions.canViewSensitiveInfo(user);
  const canViewShippingInfo = shopifyOrderPermissions.canViewShippingInfo(user);
  const canViewFinancialData = shopifyOrderPermissions.canViewFinancialData(user);
  const canViewBaseCost = shopifyOrderPermissions.canViewBaseCost(user);

  const handleRefreshTrackingInfo = useCallback(async (trackerId) => {
    if (!trackerId || refreshingTrackerIds.has(trackerId)) return;

    setRefreshingTrackerIds(prev => new Set([...prev, trackerId]));

    try {
      const result = await OrderService.refreshTrackingInfo([trackerId]);

      if (result.success) {
        const { processedCount, skippedCount } = result.data;

        if (processedCount > 0) {
          message.success(`Refreshed tracking info successfully (${processedCount} updated)`);
        } else if (skippedCount > 0) {
          message.info(`Tracking info already up to date`);
        } else {
          message.warning(`Failed to refresh tracking info`);
        }

        if (onPaginationChange) {
          onPaginationChange(pagination.current, pagination.pageSize);
        } else {
          dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
        }
      } else {
        message.error(result.message || 'Failed to refresh tracking info');
      }
    } catch (error) {
      console.error('Error refreshing tracking info:', error);
      message.error('Error refreshing tracking info');
    } finally {
      setRefreshingTrackerIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackerId);
        return newSet;
      });
    }
  }, [dispatch, onPaginationChange, pagination.current, pagination.pageSize, refreshingTrackerIds]);

  // Handle sync fulfillment info for single order (Shopify)
  const handleSyncFulfillmentInfo = useCallback(async (fulfillmentId) => {
    if (!fulfillmentId || syncingFulfillmentIds.has(fulfillmentId)) return;

    setSyncingFulfillmentIds(prev => new Set([...prev, fulfillmentId]));

    try {
      const result = await OrderService.syncShopifySingleFulfillmentInfo(fulfillmentId);

      if (result.success) {
        message.success(`Successfully synced fulfillment info for ${fulfillmentId}`);
        if (onPaginationChange) {
          onPaginationChange(pagination.current, pagination.pageSize);
        } else {
          dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
        }
      } else {
        message.error(result.message || `Failed to sync fulfillment info for ${fulfillmentId}`);
      }
    } catch (error) {
      console.error('Error syncing fulfillment info:', error);
      message.error('Error syncing fulfillment info');
    } finally {
      setSyncingFulfillmentIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(fulfillmentId);
        return newSet;
      });
    }
  }, [syncingFulfillmentIds, pagination, onPaginationChange, dispatch]);

  // Use the extracted options utilities - use shopifyCategories instead of categories
  const printerOptions = usePrinterOptions(printers);
  const categoryOptions = useCategoryOptions(shopifyCategories); // Use shopifyCategories for Shopify orders
  const userOptions = useUserOptions(users);

  // Ensure dataCommon is loaded - only once on mount
  useEffect(() => {
    dispatch(getDataCommon());
  }, [dispatch]);

  // Create the cell save handler
  const handleCellSave = useCallback(
    (record, column, value) => {
      const originalHandler = createCellSaveHandler(dispatch, pagination, fetchOrderItems, false);
      return originalHandler(record?.id, column, value);
    },
    [dispatch, pagination, fetchOrderItems]
  );

  // Memoized processed data
  const processedData = useMemo(() => {
    if (!orderItems || orderItems.length === 0) return [];

    // Group by order for rowspan functionality
    const sortedOrderItems = [...orderItems].sort((a, b) => {
      // IMPORTANT: Preserve backend intent (newest Purchase Date first)
      // Frontend grouping must not reorder by store/order name.
      const purchasedAtA = a.order?.purchasedAt ? dayjs(a.order.purchasedAt).valueOf() : null;
      const purchasedAtB = b.order?.purchasedAt ? dayjs(b.order.purchasedAt).valueOf() : null;

      // NULLS LAST
      if (purchasedAtA === null && purchasedAtB !== null) return 1;
      if (purchasedAtA !== null && purchasedAtB === null) return -1;
      if (purchasedAtA !== null && purchasedAtB !== null && purchasedAtA !== purchasedAtB) {
        return purchasedAtB - purchasedAtA; // DESC
      }

      // Stable tie-breakers
      const storeA = a.order?.store?.storeName || '';
      const storeB = b.order?.store?.storeName || '';
      const orderA = a.order?.shopifyOrderName || '';
      const orderB = b.order?.shopifyOrderName || '';

      if (storeA !== storeB) return storeA.localeCompare(storeB);
      return orderA.localeCompare(orderB);
    });

    // Group by orderId (use Map to preserve insertion order)
    const groupedData = new Map();
    for (const item of sortedOrderItems) {
      const orderId = item.shopifyOrderId;
      if (!groupedData.has(orderId)) groupedData.set(orderId, []);
      groupedData.get(orderId).push(item);
    }

    // Create data with spans
    const result = [];
    groupedData.forEach((orderItems) => {
      const rowSpan = orderItems.length;
      orderItems.forEach((item, index) => {
        result.push({
          ...item,
          orderRowSpan: index === 0 ? rowSpan : 0,
        });
      });
    });

    return result;
  }, [orderItems]);

  // Handle table change
  const handleTableChange = (paginationParams, filters, sorter) => {
    if (onPaginationChange) {
      onPaginationChange(paginationParams.current, paginationParams.pageSize);
    }
  };

  // Row selection handlers
  const handleRowSelectionChange = useCallback((newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  }, []);
  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: handleRowSelectionChange,
    preserveSelectedRowKeys: true,
    columnWidth: 32,
    fixed: true,
  }), [selectedRowKeys, handleRowSelectionChange]);

  // Get variant image with priority - matching ShopifyVariantsList.js
  const getVariantImage = useCallback((record) => {
    if (record.variant?.imageId && record.variant?.product?.images) {
      const image = record.variant.product.images.find(img =>
        img.id.toString() === record.variant.imageId.toString()
      );
      if (image) return image.src || image.url;
    }

    // Priority 2: Fallback to first product image from variant
    if (record.variant?.product?.images && record.variant.product.images.length > 0) {
      return record.variant.product.images[0].src || record.variant.product.images[0].url;
    }

    // Priority 3: Fallback to imageUrl field if exists (legacy support)
    if (record.imageUrl) {
      return record.imageUrl;
    }

    return null;
  }, []);

  // Build storefront product URL (myshopify domain redirects to the store's primary domain)
  const getStorefrontUrl = useCallback((record) => {
    const domain = record.order?.store?.shopifyDomain;
    const handle = record.variant?.product?.handle;
    if (!domain || !handle) return null;

    const variantId = String(record.variantId || '').split('/').pop();
    return `https://${domain}/products/${handle}${variantId ? `?variant=${variantId}` : ''}`;
  }, []);

  // Columns definition - matching OrdersList.js structure
  const columns = useMemo(() => [
    {
      title: '#',
      key: 'index',
      fixed: 'left',
      width: 60,
      render: (_, record, index) => {
        const currentIndex = (pagination.current - 1) * pagination.pageSize + index + 1;
        return currentIndex;
      },
    },
    {
      title: 'Order ID',
      key: 'orderName',
      dataIndex: ['order', 'shopifyOrderName'],
      width: 150,
      fixed: 'left',
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (text) => (
        <Text strong style={{ color: '#1890ff' }}>{text || '-'}</Text>
      ),
    },
    {
      title: <Tooltip title="Nhân viên hỗ trợ">Supporter</Tooltip>,
      key: 'supporter',
      dataIndex: ['order', 'supporterId'],
      width: 150,
      fixed: 'left',
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (supporterId, record) => (
        <EditableCell
          value={supporterId}
          record={record}
          column="supporterId"
          onSave={handleCellSave}
          options={userOptions}
          placeholder="Select supporter..."
          loading={isLoading}
          inputType="select"
        />
      ),
    },

    {
      title: <Tooltip title="Danh mục sản phẩm">Category</Tooltip>,
      key: 'category',
      dataIndex: 'categoryId',
      width: 150,
      fixed: 'left',
      render: (categoryId, record) => (
        <EditableCell
          value={categoryId}
          record={record}
          column="category"
          onSave={handleCellSave}
          options={categoryOptions}
          placeholder="Select category..."
          loading={isLoading}
          inputType="select"
        />
      ),
    },
    {
      title: 'Store',
      key: 'store',
      dataIndex: ['order', 'store', 'storeName'],
      width: 120,
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (text) => (
        <span>
          {canViewSensitiveInfo ? (text || '-') : <Text style={{ fontSize: 11, color: '#bbb', letterSpacing: 2 }}>***</Text>}
        </span>
      ),
    },
    {
      title: <Tooltip title="Ngày đặt hàng (giờ Mỹ)"><span>🇺🇸 Purchase Date</span></Tooltip>,
      dataIndex: ['order', 'purchasedAt'],
      key: 'purchaseDate',
      width: 170,
      align: 'center',
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (text) => {
        if (!text) return <span className="no-line-through">-</span>;

        // Convert to US Pacific Time and format
        const usTime = dayjs(text).tz('America/Los_Angeles');
        return (
          <span className="no-line-through">
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 10, color: '#666' }}>
                {usTime.format('hh:mm A')} PT
              </div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                {usTime.format('DD/MM/YYYY')}
              </div>
            </div>
          </span>
        );
      },
    },
    {
      title: 'SKU',
      key: 'sku',
      dataIndex: 'sku',
      width: 360,
      render: (text, record) => {
        const imageUrl = getVariantImage(record);
        const storefrontUrl = canViewSensitiveInfo && text ? getStorefrontUrl(record) : null;
        return (
          <Row style={{ flexFlow: 'nowrap' }} align='middle' gutter={8}>
            <Col>
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={record.title}
                  width={35}
                  height={35}
                  style={{
                    objectFit: 'cover',
                    borderRadius: 4
                  }}
                />
              ) : (
                <div style={{
                  width: 35,
                  height: 35,
                  backgroundColor: '#f0f0f0',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BoxPlotOutlined style={{ color: '#bfbfbf' }} />
                </div>
              )}
            </Col>
            <Col>
              {record.variantTitle && (
                <>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {record.variantTitle}
                  </Text>
                  <br />
                </>
              )}
              {storefrontUrl ? (
                <a
                  className="sku-storefront-link"
                  href={storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              ) : (
                <Text>{text || '-'}</Text>
              )}
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Product Title',
      key: 'productTitle',
      dataIndex: 'title',
      width: 400,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <span style={{ fontSize: 11, color: '#999' }}>{record.shopifyLineItemId || '-'}</span><br />
          <Tooltip title={text}>
            <Paragraph className="no-line-through" style={{ marginBottom: 0 }} ellipsis={{ rows: 1 }} >{text ? text : '-'}</Paragraph>
          </Tooltip>
        </div>
      ),
    },
    {
      title: <Tooltip title="Tùy chỉnh">Customization</Tooltip>,
      key: 'customization',
      width: 125,
      align: 'center',
      render: (text, record) => {
        // Check if record is customized based on isCustomized field
        if (record.isCustomized === true) {
          return (
            <Button
              variant="dashed"
              color="primary"
              size="small"
              icon={<BgColorsOutlined />}
              onClick={() => {
                setSelectedCustomization(record);
                setIsCustomizationModalVisible(true);
              }}
            >
              Details
            </Button>
          );
        }

        // No customization
        return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
      }
    },
    {
      title: 'Note',
      key: 'note',
      dataIndex: 'note',
      width: 200,
      render: (text, record) => (
        <EditableCellWithNoteTemplates
          value={text}
          record={record}
          column="note"
          onSave={handleCellSave}
          placeholder="Add note..."
          loading={isLoading}
          inputType="note-autocomplete"
        />
      ),
    },
    {
      title: <Tooltip title="Nhà in">Printer</Tooltip>,
      key: 'printer',
      width: 170,
      align: 'center',
      render: (text, record) => {
        const printerId = record.printerId;

        return (
          <EditableCell
            value={printerId}
            record={record}
            column="printer"
            onSave={handleCellSave}
            options={printerOptions}
            placeholder="Select printer..."
            loading={isLoading}
          />
        );
      }
    },
    {
      title: <Tooltip title="Giá gốc">Base Cost</Tooltip>,
      key: 'baseCost',
      width: 120,
      align: 'right',
      render: (text, record) => {
        return (
          <EditableCell
            value={record.baseCost}
            record={record}
            column="baseCost"
            onSave={handleCellSave}
            placeholder="Enter cost..."
            loading={isLoading}
            inputType="number"
            prefix="$"
          />
        );
      }
    },
    {
      title: 'Qty',
      key: 'quantity',
      dataIndex: 'quantity',
      width: 60,
      align: 'center',
      render: (qty) => <Text strong>{qty || 0}</Text>,
    },
    {
      title: 'Item Price',
      key: 'itemPrice',
      dataIndex: 'price',
      width: 100,
      align: 'right',
      render: (price) => {
        const priceNum = parseFloat(price) || 0;
        return <Text>${priceNum.toFixed(2)}</Text>;
      },
    },
    {
      title: 'Total Price',
      key: 'totalPrice',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const priceNum = parseFloat(record.price) || 0;
        const quantityNum = parseInt(record.quantity) || 0;
        const total = priceNum * quantityNum;
        return <Text strong>${total.toFixed(2)}</Text>;
      },
    },
    {
      title: <Tooltip title="Tổng discount được áp dụng cho line item">Discount</Tooltip>,
      key: 'discount',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const discountNum = parseFloat(record.totalDiscount) || 0;
        if (discountNum === 0) return <Text style={{ color: '#bfbfbf' }}>-</Text>;
        return <Text style={{ color: '#f5222d' }}>-${discountNum.toFixed(2)}</Text>;
      },
    },
    {
      title: <Tooltip title="Doanh thu sau discount (price × qty - discount)">Net Sales</Tooltip>,
      key: 'netSales',
      width: 110,
      align: 'right',
      render: (_, record) => {
        const priceNum = parseFloat(record.price) || 0;
        const quantityNum = parseInt(record.quantity) || 0;
        const discountNum = parseFloat(record.totalDiscount) || 0;
        const netSales = priceNum * quantityNum - discountNum;
        return (
          <Text strong style={{ color: discountNum > 0 ? '#52c41a' : undefined }}>
            ${netSales.toFixed(2)}
          </Text>
        );
      },
    },
    {
      title: <Tooltip title="Phí vận chuyển của cả đơn hàng">Shipping</Tooltip>,
      key: 'shippingFee',
      width: 110,
      align: 'right',
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (_, record) => {
        if (!record.orderRowSpan) return null;
        const shipping = parseFloat(record.order?.totalShipping) || 0;
        if (shipping === 0) return <Text style={{ color: '#bfbfbf' }}>-</Text>;
        return <Text style={{ color: '#1890ff' }}>${shipping.toFixed(2)}</Text>;
      },
    },
    {
      title: <Tooltip title="Tổng doanh thu (Net Sales tất cả items + Shipping Fee)">Total Sales</Tooltip>,
      key: 'totalSales',
      width: 120,
      align: 'right',
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (_, record) => {
        if (!record.orderRowSpan) return null;
        const orderId = record.order?.id;
        const orderItems = processedData.filter(item => item.order?.id === orderId);
        const netSalesSum = orderItems.reduce((sum, item) => {
          const price = parseFloat(item.price) || 0;
          const qty = parseInt(item.quantity) || 0;
          const discount = parseFloat(item.totalDiscount) || 0;
          return sum + (price * qty - discount);
        }, 0);
        const shipping = parseFloat(record.order?.totalShipping) || 0;
        const total = netSalesSum + shipping;
        return <Text strong style={{ color: '#52c41a' }}>${total.toFixed(2)}</Text>;
      },
    },
    {
      title: <Tooltip title="Trạng thái tùy chỉnh">Customization<br /> Status</Tooltip>,
      dataIndex: 'customizationStatus',
      key: 'customizationStatus',
      width: 230,
      align: 'center',
      render: (status, record) => {
        const customizationStatusOptions = [
          { value: 'NoCustomization', label: 'No Customization' },
          { value: 'TaskCreated', label: 'Task Created' },
          { value: 'Doing', label: 'Doing' },
          { value: 'InternalReview', label: 'Internal Review' },
          { value: 'Rework', label: 'Rework' },
          { value: 'MockupReady', label: 'Mockup Ready' },
          { value: 'MockupSent', label: 'Mockup Sent' },
          { value: 'Confirming', label: 'Confirming' },
          { value: 'CustomerConfirmed', label: 'Customer Confirmed' },
          { value: 'ConfirmedNoReply', label: 'Confirmed No Reply' },
          { value: 'AutoFulfill', label: 'Auto Fulfill' }
        ];

        // Be resilient to different API naming conventions / legacy fields
        const trelloCardIdOrUrl =
          record?.trelloCardId ||
          record?.trelloUrl ||
          record?.trello_card_id ||
          record?.trello_url ||
          record?.customizationInfo?.trelloCardId ||
          record?.customizationInfo?.trelloUrl ||
          record?.customizationInfo?.trello_card_id ||
          record?.customizationInfo?.trello_url;

        const googleDriveUrl =
          record?.googleDriveUrl ||
          record?.customizationStorageUrl ||
          record?.google_drive_url ||
          record?.customization_storage_url ||
          record?.customizationInfo?.googleDriveUrl ||
          record?.customizationInfo?.customizationStorageUrl ||
          record?.customizationInfo?.google_drive_url ||
          record?.customizationInfo?.customization_storage_url;

        return (
          <EditableCell
            value={status}
            record={record}
            column="customizationStatus"
            onSave={handleCellSave}
            options={customizationStatusOptions}
            placeholder="Select status..."
            loading={isLoading}
            inputType="select"
            displayComponent={(currentValue) => (
              <span className="no-line-through">
                <MemoizedCustomizationStatus
                  status={currentValue}
                  trelloUrl={normalizeTrelloUrl(trelloCardIdOrUrl)}
                  customizationStorageUrl={googleDriveUrl}
                  mockupSentAt={record.mockupSentAt}
                />
              </span>
            )}
          />
        );
      }
    },
    {
      title: 'Financial Status',
      key: 'financialStatus',
      dataIndex: ['order', 'financialStatus'],
      align: 'center',
      width: 130,
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (status) => {
        const colorMap = {
          'PAID': 'success',
          'AUTHORIZED': 'processing',
          'PARTIALLY_PAID': 'warning',
          'PARTIALLY_REFUNDED': 'warning',
          'REFUNDED': 'error',
          'VOIDED': 'default',
          'PENDING': 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status || '-'}</Tag>;
      },
    },
    {
      title: <Tooltip title="Trạng thái thực hiện">Fulfillment Status</Tooltip>,
      dataIndex: 'fulfillmentStatus',
      key: 'fulfillmentStatus',
      width: 150,
      align: 'center',
      render: (status, record) => {
        const fulfillmentStatusOptions = [
          { value: 'Unfulfilled', label: 'Unfulfilled' },
          { value: 'Submitted', label: 'Submitted' },
          { value: 'Fulfilled', label: 'Fulfilled' },
          { value: 'Shipped', label: 'Shipped' },
          { value: 'Canceled', label: 'Canceled' },
          { value: 'Failed', label: 'Failed' }
        ];

        // Status color mapping
        const getStatusColor = (status) => {
          switch (status) {
            case 'Unfulfilled':
              return 'default';
            case 'Fulfilled':
              return 'blue';
            case 'Shipped':
              return 'green';
            case 'Canceled':
              return 'red';
            case 'Submitted':
              return 'orange';
            case 'Failed':
              return 'red';
            default:
              return 'default';
          }
        };

        return (
          <EditableCell
            value={status}
            record={record}
            column="fulfillmentStatus"
            onSave={handleCellSave}
            options={fulfillmentStatusOptions}
            placeholder="Select status..."
            loading={isLoading}
            inputType="select"
            displayComponent={(currentValue) => (
              <span className="no-line-through"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  width: '170px'
                }}
              >
                {currentValue ? (
                  <>
                    <Tag color={getStatusColor(currentValue)} style={{ marginRight: 0, width: 100, textAlign: 'center' }}>
                      {currentValue}
                    </Tag>
                    {currentValue === 'Failed' && record.fulfillmentErrorReason && (
                      <Tooltip title={record.fulfillmentErrorReason}>
                        <ExclamationCircleOutlined style={{ color: 'red', cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#999', fontSize: 11 }}>-</span>
                )}
              </span>
            )}
          />
        );
      }
    },
    {
      title: <Tooltip title="Mã định danh fulfillment">Fulfillment ID</Tooltip>,
      dataIndex: 'fulfillmentId',
      key: 'fulfillmentId',
      width: 190,
      align: 'center',
      render: (fulfillmentId, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <EditableCell
              value={fulfillmentId}
              record={record}
              column="fulfillmentId"
              onSave={handleCellSave}
              placeholder="Enter fulfillment ID..."
              loading={isLoading}
              inputType="text"
              displayComponent={(currentValue) => (
                <span className="no-line-through">
                  {currentValue ? (
                    <Text
                      style={{
                        color: '#1890ff',
                        cursor: 'pointer'
                      }}
                      copyable={{ text: currentValue }}
                    >
                      {currentValue}
                    </Text>
                  ) : (
                    <span style={{ color: '#999', fontSize: 11 }}>-</span>
                  )}
                </span>
              )}
            />
            {fulfillmentId && canUpdateOrders && (
              <Tooltip title={`Sync fulfillment info for ${fulfillmentId}`}>
                <Button
                  type="text"
                  size="small"
                  icon={<SyncOutlined spin={syncingFulfillmentIds.has(fulfillmentId)} />}
                  loading={syncingFulfillmentIds.has(fulfillmentId)}
                  disabled={syncingFulfillmentIds.has(fulfillmentId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSyncFulfillmentInfo(fulfillmentId);
                  }}
                  style={{
                    color: '#52c41a',
                    padding: '0 4px',
                    height: 20,
                    minWidth: 20
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: <Tooltip title="Xác nhận đã giao hàng">Confirm Shipment</Tooltip>,
      dataIndex: 'isConfirmShipped',
      key: 'isConfirmShipped',
      width: 135,
      align: 'center',
      render: (isConfirmed, record) => {
        const confirmShipmentOptions = [
          { value: true, label: 'Confirmed' },
          { value: false, label: 'Pending' }
        ];

        return (
          <EditableCell
            value={isConfirmed}
            record={record}
            column="isConfirmShipped"
            onSave={handleCellSave}
            options={confirmShipmentOptions}
            placeholder="Select status..."
            loading={isLoading}
            inputType="select"
            displayComponent={(currentValue) => (
              <span className="no-line-through">
                {currentValue ? (
                  <Tag color="green" style={{ marginRight: 0, fontSize: 11, width: 85, textAlign: 'center' }}>
                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                    Confirmed
                  </Tag>
                ) : (
                  <Tag color="orange" style={{ marginRight: 0, fontSize: 11, width: 85, textAlign: 'center' }}>
                    Pending
                  </Tag>
                )}
              </span>
            )}
          />
        );
      }
    },
    {
      title: <Tooltip title="Shipping Information">Shipping info</Tooltip>,
      key: 'shippingInfo',
      width: 120,
      align: 'center',
      render: (text, record) => {
        const hasShippingInfo = record.order?.shippingName;
        const addressValidation = record.order?.shippingAddressValidation;
        const hasAddressWarning = addressValidation === 'WARNING' || addressValidation === 'ERROR';

        const addressWarningTooltip = addressValidation === 'ERROR'
          ? 'Shopify: Địa chỉ giao hàng bị lỗi nghiêm trọng, cần kiểm tra lại'
          : 'Shopify: Địa chỉ giao hàng có thể không chính xác, Shopify đang gợi ý địa chỉ khác';

        // Show clickable button/text for all Shopify orders
        if (hasShippingInfo) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Button
                size="small"
                variant="dashed"
                color="primary"
                icon={<CarOutlined />}
                onClick={() => {
                  setSelectedOrder(record.order);
                  setIsModalVisible(true);
                }}
              >
                View
              </Button>
              {hasAddressWarning && (
                <Tooltip title={addressWarningTooltip}>
                  <ExclamationCircleOutlined style={{ color: addressValidation === 'ERROR' ? '#ff4d4f' : '#fa8c16', fontSize: 14, cursor: 'help' }} />
                </Tooltip>
              )}
            </div>
          );
        } else {
          // Show clickable "Waiting for sync" text with hover effect like EditableCell
          return (
            <div
              className="editable-cell"
              onClick={() => {
                setSelectedOrder(record.order);
                setIsModalVisible(true);
              }}
            >
              <span style={{ color: '#999', fontSize: 11 }}>
                <SyncOutlined /> Waiting for sync
              </span>
            </div>
          );
        }
      },
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      })
    },
    {
      title: <Tooltip title="Phương thức giao hàng">Delivery Method</Tooltip>,
      key: 'deliveryMethod',
      dataIndex: ['order', 'shippingMethod'],
      width: 140,
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      }),
      render: (text) => text ? <Tag color="blue">{text}</Tag> : <span>-</span>,
    },
    {
      title: <Tooltip title="Số theo dõi vận chuyển">Tracking Number</Tooltip>,
      key: 'trackingNumber',
      width: 240,
      render: (_, record) => {
        const trackingInfo = record.trackingInfo;
        const hasTracker = trackingInfo && trackingInfo.trackerId;
        const hasTrackingNumber = record.trackingNumber;
        const trackerId = trackingInfo?.trackerId;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {hasTrackingNumber && (
              <Tooltip
                title={
                  hasTracker
                    ? `Tracking has been added to Ship24 with ID: ${trackingInfo.trackerId}`
                    : 'Tracking has not been added to Ship24'
                }
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: hasTracker ? '#52c41a' : '#d9d9d9',
                    flexShrink: 0
                  }}
                />
              </Tooltip>
            )}

            <EditableCell
              value={record.trackingNumber}
              record={record}
              column="trackingNumber"
              onSave={handleCellSave}
              placeholder="Enter tracking..."
              loading={isLoading}
              inputType="text"
            />

            {trackerId && canUpdateOrders && (
              <Tooltip title={`Refresh tracking info from Ship24`}>
                <Button
                  type="text"
                  size="small"
                  icon={<SyncOutlined spin={refreshingTrackerIds.has(trackerId)} />}
                  loading={refreshingTrackerIds.has(trackerId)}
                  disabled={refreshingTrackerIds.has(trackerId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshTrackingInfo(trackerId);
                  }}
                  style={{
                    color: '#1890ff',
                    padding: '0 4px',
                    height: 20,
                    minWidth: 20
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: <Tooltip title="Hãng vận chuyển">Carrier</Tooltip>,
      key: 'carrier',
      width: 130,
      align: 'left',
      render: (_, record) => {
        const carrierOptions = [
          { value: 'USPS', label: 'USPS' },
          { value: 'UPS', label: 'UPS' },
          { value: 'Yanwen', label: 'Yanwen' },
          { value: 'Yunexpress', label: 'Yunexpress' },
          { value: 'FedEx', label: 'FedEx' },
          { value: 'DHL', label: 'DHL' },
          { value: 'AmazonLogistics', label: 'Amazon Logistics' },
          { value: 'OnTrac', label: 'OnTrac' },
          { value: 'Lasership', label: 'Lasership' },
          { value: 'PostalService', label: 'Postal Service' },
          { value: 'Other', label: 'Other' }
        ];

        return (
          <EditableCell
            value={record.carrier}
            record={record}
            column="carrier"
            onSave={handleCellSave}
            placeholder="Select carrier..."
            loading={isLoading}
            inputType="autocomplete"
            autoCompleteOptions={carrierOptions}
          />
        );
      }
    },
    {
      title: <Tooltip title="Last Mile Tracking">Last Mile</Tooltip>,
      key: 'lastMile',
      width: 200,
      ellipsis: true,
      render: (text, record) => {
        const trackingInfo = record.trackingInfo;

        if (!trackingInfo || (!trackingInfo.lastMileTracking && !trackingInfo.lastMileCarrier)) {
          return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
        }

        return (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {trackingInfo.lastMileCarrier && (
                <div style={{
                  fontSize: 10,
                  color: '#666',
                  fontWeight: 'bold',
                  marginBottom: 2,
                  flex: 1
                }}>
                  {trackingInfo.lastMileCarrier}
                </div>
              )}
              {trackingInfo.isUpdatedLastMile && (
                <Tooltip title="Last mile information has been updated">
                  <WarningOutlined
                    style={{
                      color: '#fa8c16',
                      fontSize: 12,
                      marginBottom: 2
                    }}
                  />
                </Tooltip>
              )}
            </div>
            {trackingInfo.lastMileTracking && (
              <Tooltip title={trackingInfo.lastMileTracking}>
                <Text
                  style={{
                    fontSize: 11,
                    color: '#1890ff',
                    fontFamily: 'monospace',
                    display: 'block'
                  }}
                  ellipsis={{ tooltip: false }}
                  copyable={{ text: trackingInfo.lastMileTracking }}
                >
                  {trackingInfo.lastMileTracking}
                </Text>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: <Tooltip title="Xác nhận đã gửi tracking cho khách hàng">Confirm Ship</Tooltip>,
      key: 'isConfirmShipped',
      width: 120,
      align: 'center',
      render: (text, record) => {
        const isConfirmed = record.isConfirmShipped;

        if (isConfirmed === null || isConfirmed === undefined) {
          return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
        }

        return (
          <Tag
            color={isConfirmed ? 'green' : 'orange'}
            style={{ fontSize: 11 }}
          >
            {isConfirmed ? 'Confirmed' : 'Pending'}
          </Tag>
        );
      }
    },
    {
      title: <Tooltip title="Trạng thái vận chuyển">Shipment Status</Tooltip>,
      key: 'shipmentStatus',
      width: 170,
      align: 'center',
      render: (text, record) => {
        const trackingInfo = record.trackingInfo;
        const currentStatus = trackingInfo?.lastStatus;
        const lastScrapedAt = trackingInfo?.lastScrapedAt;

        const shipmentStatusOptions = [
          { value: 'info_received', label: 'Info. Received' },
          { value: 'in_transit', label: 'In Transit' },
          { value: 'out_for_delivery', label: 'Out for Delivery' },
          { value: 'failed_attempt', label: 'Failed Attempt' },
          { value: 'available_for_pickup', label: 'To Pick Up' },
          { value: 'delivered', label: 'Delivered' },
          { value: 'exception', label: 'Exception' },
          { value: 'pending', label: 'Pending' }
        ];

        // Status color mapping
        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case 'delivered':
              return 'green';
            case 'out_for_delivery':
              return 'blue';
            case 'in_transit':
              return 'processing';
            case 'info_received':
              return 'cyan';
            case 'available_for_pickup':
              return 'purple';
            case 'failed_attempt':
              return 'orange';
            case 'exception':
              return 'red';
            case 'pending':
              return 'gold';
            default:
              return 'default';
          }
        };

        // Format status text for display
        const formatStatus = (status) => {
          if (!status) return 'Unknown';
          return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        };

        // Get icon for status
        const getStatusIcon = (status) => {
          switch (status?.toLowerCase()) {
            case 'in_transit':
              return <TruckIcon style={{ marginRight: 4, fontSize: 14 }} />;
            case 'delivered':
              return <DeliveredIcon style={{ marginRight: 4, fontSize: 14 }} />;
            case 'out_for_delivery':
              return <CarOutlined style={{ marginRight: 4, fontSize: 14 }} />;
            default:
              return null;
          }
        };

        return (
          <EditableCell
            value={currentStatus}
            record={record}
            column="shipmentStatus"
            onSave={handleCellSave}
            options={shipmentStatusOptions}
            placeholder="Select status..."
            loading={isLoading}
            inputType="select"
            displayComponent={(currentValue) => (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 10,
                  color: '#999',
                  lineHeight: 1.2
                }}>
                  Scraped at: {lastScrapedAt ? dayjs(lastScrapedAt).format('MM/DD HH:mm') : '--/--'}
                </div>
                {currentValue ? (
                  <Tag
                    color={getStatusColor(currentValue)}
                    style={{
                      marginTop: 1,
                      marginRight: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {getStatusIcon(currentValue)}
                    {formatStatus(currentValue)}
                  </Tag>
                ) : (
                  <span style={{ color: '#999', fontSize: 11 }}>Click to set</span>
                )}
              </div>
            )}
          />
        );
      }
    },
    {
      title: <Tooltip title="Chi tiết vận chuyển">Shipment Details</Tooltip>,
      key: 'shipmentDetails',
      width: 320,
      align: 'left',
      ellipsis: true,
      render: (text, record) => {
        const trackingInfo = record.trackingInfo;

        if (!trackingInfo || !trackingInfo.scrapeData || !trackingInfo.scrapeData.events || trackingInfo.scrapeData.events.length === 0) {
          return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
        }

        // Get the first event (latest event)
        const firstEvent = trackingInfo.scrapeData.events[0];

        if (!firstEvent) {
          return <span style={{ color: '#999', fontSize: 11 }}>-</span>;
        }

        return (
          <div
            style={{
              lineHeight: 1.3,
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
            onClick={() => {
              setSelectedTrackingInfo(trackingInfo);
              setIsShippingTimelineModalVisible(true);
            }}
          >
            {firstEvent.location && (
              <div style={{
                color: '#999',
                fontSize: 10,
                display: 'flex',
                alignItems: 'center'
              }}>
                <b>{firstEvent.occurrenceDatetime ? dayjs(firstEvent.occurrenceDatetime).tz('America/Los_Angeles').format('MM/DD HH:mm') : '--:--'}:</b> {firstEvent.location}
              </div>
            )}
            <a style={{
              color: '#1890ff',
              marginBottom: 2,
              textDecoration: 'underline',
            }}>
              {firstEvent.status}
            </a>
          </div>
        );
      }
    },
    {
      title: <Tooltip title="Ngày cập nhật">Last Update</Tooltip>,
      dataIndex: ['order', 'syncedAt'],
      key: 'lastUpdate',
      width: 160,
      align: 'right',
      render: (text) => <span className="no-line-through">{text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
      })
    },
    {
      title: (
        <Tooltip title="Overview trạng thái đơn hàng">
          <span>
            Status Overview
            <StatusOverviewTooltip />
          </span>
        </Tooltip>
      ),
      dataIndex: ['order', 'financialStatus'],
      key: 'statusOverview',
      width: 155,
      align: 'center',
      fixed: 'right',
      className: 'status-overview-cell',
      render: (financialStatus, record) => (
        <span className="no-line-through">
          <StatusOverview
            record={record}
            orderStatus={financialStatus}
            onMessageClick={null}
            allOrderItems={processedData}
          />
        </span>
      ),
      onCell: (record) => ({
        rowSpan: record.orderRowSpan,
        className: 'status-overview-cell'
      })
    }
  ].filter(col => {
    const shippingColumns = ['shippingInfo', 'trackingNumber', 'carrier', 'lastMile', 'shipmentStatus', 'shipmentDetails'];
    const financialColumns = ['itemPrice', 'totalPrice', 'discount', 'netSales', 'shippingFee', 'totalSales'];
    if (shippingColumns.includes(col.key) && !canViewShippingInfo) return false;
    if (financialColumns.includes(col.key) && !canViewFinancialData) return false;
    if (col.key === 'baseCost' && !canViewBaseCost) return false;
    return true;
  }), [pagination, handleCellSave, categoryOptions, printerOptions, userOptions, getVariantImage, getStorefrontUrl, isLoading, processedData, handleSyncFulfillmentInfo, syncingFulfillmentIds, canUpdateOrders, canViewSensitiveInfo, canViewShippingInfo, canViewFinancialData, canViewBaseCost]);

  // Bulk action menu items
  const bulkActionMenuItems = useMemo(() => {
    const items = [];

    if (canBulkEdit) {
      items.push({
        key: 'bulk-edit',
        label: `Bulk Edit (${selectedRowKeys.length})`,
        icon: <EditOutlined />,
        disabled: selectedRowKeys.length === 0,
      });
    }

    if (canSendMockup) {
      items.push({
        key: 'sendMockup',
        label: 'Send Mockup',
        icon: <SendOutlined />,
        disabled: selectedRowKeys.length === 0 || !selectedRowKeys.every(key => {
          const record = processedData.find(item => item.id === key);
          return record && record.customizationStatus === 'MockupReady';
        })
      });
    }

    if (canSendCustomMessage) {
      items.push({
        key: 'sendCustomMessage',
        label: 'Send Custom Message',
        icon: <MessageOutlined />,
        disabled: selectedRowKeys.length === 0
      });
    }

    if (canFulfillToPrinter) {
      items.push({
        key: 'fulfillToPrinter',
        label: 'Fulfill to Printer',
        icon: <BoxPlotOutlined />,
        disabled: selectedRowKeys.length === 0
      });
    }

    if (canConfirmShipment) {
      items.push({
        key: 'confirmShipment',
        label: 'Confirm Shipment',
        icon: <CheckCircleOutlined />,
        disabled: selectedRowKeys.length === 0 || !selectedRowKeys.every(key => {
          const record = processedData.find(item => item.id === key);
          return record && record.trackingNumber && record.carrier && !record.isConfirmShipped;
        })
      });
    }

    return items;
  }, [selectedRowKeys, canBulkEdit, canSendMockup, canSendCustomMessage, canFulfillToPrinter, canConfirmShipment, processedData]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(async (updateData) => {
    if (!canBulkEdit) {
      message.error('You do not have permission to perform bulk edit');
      return;
    }

    try {
      const response = await OrderService.bulkUpdateShopifyOrderItems({
        ids: selectedRowKeys,
        updateData
      });

      if (response.success) {
        message.success(`Successfully updated ${response.count || selectedRowKeys.length} items`);
        setIsBulkEditModalVisible(false);
        // Refresh data
        if (onPaginationChange) {
          onPaginationChange(pagination.current, pagination.pageSize);
        }
      }
    } catch (error) {
      message.error(error.message || 'Failed to update items');
    }
  }, [selectedRowKeys, canBulkEdit, pagination, onPaginationChange]);

  const handleBulkActionMenuClick = useCallback(({ key }) => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.warning('Please select at least one order');
      return;
    }

    switch (key) {
      case 'bulk-edit':
        if (!canBulkEdit) {
          message.error('You do not have permission to bulk edit');
          return;
        }
        setIsBulkEditModalVisible(true);
        break;

      case 'sendMockup':
        if (!canSendMockup) {
          message.warning('Bạn không có quyền gửi mockup');
          return;
        }

        // Filter selected records that are MockupReady
        const eligibleOrders = processedData
          .filter(item => selectedRowKeys.includes(item.id) && item.customizationStatus === 'MockupReady')
          .map(item => ({
            id: item.id,
            lineItemId: item.shopifyLineItemId,
            orderNumber: item.order?.shopifyOrderName,
            productTitle: item.title,
            customerEmail: item.order?.email,
            storeName: item.order?.shopifyStore?.storeName,
            storeId: item.order?.shopifyStoreId
          }));

        setMockupReadyOrders(eligibleOrders);
        setIsSendMockupModalVisible(true);
        break;

      case 'sendCustomMessage':
        if (!canSendCustomMessage) {
          message.warning('Bạn không có quyền gửi custom message');
          return;
        }

        // Get all selected order items
        const selectedItems = processedData.filter(item => selectedRowKeys.includes(item.id));

        if (selectedItems.length === 0) {
          message.warning('Không có order item nào được chọn');
          return;
        }

        setSelectedOrdersForCustomMessage(selectedItems);
        setIsSendCustomMessageModalVisible(true);
        break;

      case 'fulfillToPrinter':
        if (!canFulfillToPrinter) {
          message.warning('Bạn không có quyền fulfill to printer');
          return;
        }

        const selectedOrders = processedData
          .filter(item => selectedRowKeys.includes(item.id))
          .map(item => ({
            id: item.id,
            orderItemNumber: item.shopifyLineItemId,
            orderNumber: item.order?.shopifyOrderName,
            title: item.title,
            sku: item.sku,
            customerName: item.order?.shippingName || 'N/A',
            printerName: printers?.find(p => p.id === item.printerId)?.name || null,
            customizationStatus: item.customizationStatus,
            shipAddressLine1: item.order?.shippingAddress1,
            shipCity: item.order?.shippingCity,
            shipCountryCode: item.order?.shippingCountryCode
          }));

        setSelectedOrdersForFulfill(selectedOrders);
        setIsFulfillToPrinterModalVisible(true);
        break;

      case 'confirmShipment':
        if (!canConfirmShipment) {
          message.warning('Bạn không có quyền xác nhận shipment');
          return;
        }

        // Filter selected records that have tracking number and carrier but not yet confirmed shipped
        const eligibleShipmentOrders = processedData
          .filter(item => selectedRowKeys.includes(item.id) && item.trackingNumber && item.carrier && !item.isConfirmShipped)
          .map(item => ({
            id: item.id,
            shopifyLineItemId: item.shopifyLineItemId,
            shopifyOrderName: item.order?.shopifyOrderName,
            title: item.title,
            sku: item.sku,
            customerName: item.order?.shippingName || 'N/A',
            trackingNumber: item.trackingNumber,
            carrier: item.carrier,
            lineItemId: item.shopifyLineItemId,
            storeId: item.order?.shopifyStoreId,
            lastMileTrackingNumber: item.trackingInfo?.lastMileTracking || null,
            lastMileCarrier: item.trackingInfo?.lastMileCarrier || null,
          }));

        if (eligibleShipmentOrders.length === 0) {
          message.warning('Không có order nào phù hợp để confirm shipment. Order cần có tracking number, carrier và chưa được confirm.');
          return;
        }

        setShipmentReadyOrders(eligibleShipmentOrders);
        setIsConfirmShipmentModalVisible(true);
        break;

      default:
        break;
    }
  }, [canBulkEdit, canSendMockup, canSendCustomMessage, canFulfillToPrinter, canConfirmShipment, processedData, selectedRowKeys, printers]);

  // Handle sync modal
  const handleSyncOrders = useCallback(async (params) => {
    try {
      await dispatch(syncShopifyOrders(params));
      setSyncModalVisible(false);
      // Optionally refresh orders after sync
      // dispatch(fetchOrderItems(1, pagination.pageSize));
    } catch (error) {
      // Error already handled in action with message
      console.error('Sync failed:', error);
    }
  }, [dispatch]);

  // Handle upload shipping CSV
  const handleUploadShippingCsv = useCallback(async (formData) => {
    try {
      const response = await api.post('/shopify/orders/upload-shipping-report', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        message.success(response.data.message);

        // Show details if available
        if (response.data.data) {
          const { totalRecords, updatedOrders, skippedRecords, errors } = response.data.data;

          if (errors && errors.length > 0) {
            const errorMessage = errors.slice(0, 5).map(e =>
              `${e.orderName}: ${e.reason}`
            ).join('\n');
            message.warning({
              content: `Updated ${updatedOrders}/${totalRecords} orders. Some errors occurred:\n${errorMessage}`,
              duration: 10,
            });
          } else {
            message.success(`Successfully updated ${updatedOrders} orders`);
          }
        }

        // Refresh order list
        dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
      } else {
        message.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload shipping CSV error:', error);
      message.error(error.response?.data?.message || 'Lỗi khi upload CSV');
      throw error;
    }
  }, [dispatch, pagination]);

  // Handle send custom message
  const handleSendCustomMessage = useCallback(async (data) => {
    try {
      const { orderItemIds, messageContent } = data;

      const response = await OrderService.sendShopifyCustomMessage(orderItemIds, messageContent);

      if (response && response.success) {
        message.success(`Custom message jobs created for ${response.data.totalOrders} orders`);
        setSelectedOrdersForCustomMessage([]);
        setIsSendCustomMessageModalVisible(false);

        // Optionally refresh the orders list
        if (onPaginationChange) {
          onPaginationChange(pagination.current, pagination.pageSize);
        }

        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to send custom message');
      }
    } catch (error) {
      console.error('Error sending custom message:', error);
      message.error(error.message || 'Failed to send custom message');
      throw error;
    }
  }, [pagination, onPaginationChange]);

  // Sync menu items
  const syncMenuItems = useMemo(() => [
    {
      key: 'sync-orders',
      label: 'Sync Shopify Orders',
      icon: <SyncOutlined />,
    },
    {
      key: 'upload-shipping-csv',
      label: 'Upload Shipping CSV',
      icon: <UploadOutlined />,
    },
  ], []);

  // Data menu items (placeholders)
  const dataMenuItems = useMemo(() => [
    {
      key: 'export-csv',
      label: 'Export to CSV',
      icon: <DatabaseOutlined />,
      disabled: !canExportCSV
    },
  ], [canExportCSV]);

  // Handle export CSV
  const handleExportCSV = useCallback(async () => {
    try {
      message.loading({ content: 'Đang export CSV...', key: 'export-csv', duration: 0 });

      // Build params từ Redux filters state (giống fetchOrderItems)
      const params = new URLSearchParams();
      const appendFilter = (key, value) => {
        if (Array.isArray(value)) {
          if (value.length > 0) value.forEach(v => params.append(key, v));
        } else if (value && value !== 'all') {
          params.append(key, value);
        }
      };

      if (filters?.searchText) params.append('search', filters.searchText);
      if (filters?.selectedDateRange && filters.selectedDateRange.length === 2) {
        params.append('startDate', dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD'));
        params.append('endDate', dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD'));
      }
      appendFilter('shopifyStoreId', filters?.selectedStore);
      appendFilter('financialStatus', filters?.selectedFinancialStatus);
      appendFilter('fulfillmentStatus', filters?.selectedFulfillmentStatus);
      appendFilter('categoryId', filters?.selectedCategory);
      appendFilter('collectionId', filters?.selectedCollection);
      appendFilter('printerId', filters?.selectedPrinter);
      appendFilter('supporterId', filters?.selectedSupporter);
      if (filters?.selectedTitle?.trim()) params.append('title', filters.selectedTitle.trim());
      if (filters?.selectedSku?.trim()) params.append('sku', filters.selectedSku.trim());
      if (filters?.selectedEmployeeId?.trim()) params.append('employeeId', filters.selectedEmployeeId.trim());
      if (filters?.selectedExcludeTitle?.trim()) params.append('excludeTitle', filters.selectedExcludeTitle.trim());
      if (filters?.selectedExcludeSku?.trim()) params.append('excludeSku', filters.selectedExcludeSku.trim());

      // Chuyển URLSearchParams sang object (giữ multi-value bằng cách dùng params trực tiếp)
      const exportParams = {};
      for (const key of new Set(params.keys())) {
        const values = params.getAll(key);
        exportParams[key] = values.length === 1 ? values[0] : values;
      }

      const response = await OrderService.exportShopifyOrderItemsCSV(exportParams);

      // Create blob and download file
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Get filename from response headers or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'shopify_order_items_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Export CSV thành công!', key: 'export-csv' });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      message.error({ content: 'Lỗi khi export CSV', key: 'export-csv' });
    }
  }, [filters]);

  const handleDataMenuClick = useCallback(({ key }) => {
    if (key === 'export-csv') {
      handleExportCSV();
    }
  }, [handleExportCSV]);

  const handleSyncMenuClick = useCallback(({ key }) => {
    if (key === 'sync-orders') {
      setSyncModalVisible(true);
    } else if (key === 'upload-shipping-csv') {
      setUploadCsvModalVisible(true);
    }
  }, []);

  // Handle pagination change for the small pagination component
  const handleSmallPaginationChange = (page, pageSize) => {
    if (onPaginationChange) {
      onPaginationChange(page, pageSize);
    }
  };

  // Optimized getRowClassName with useCallback - matching OrdersList.js
  const getRowClassName = useCallback((record, index) => {
    let className = index % 2 === 0 ? 'table-row-even' : 'table-row-odd';

    // Nếu đơn hàng có trạng thái Canceled, áp dụng class canceled
    if (record.order?.financialStatus === 'VOIDED' || record.order?.financialStatus === 'REFUNDED') {
      className = 'table-row-canceled';
    }

    // Kiểm tra nếu là item cuối cùng của order hiện tại dựa trên orderId
    const items = processedData;
    const nextRow = items[index + 1];
    if (!nextRow || nextRow.order?.id !== record.order?.id) {
      className += ' order-group-last-item';
    }

    return className;
  }, [processedData]);

  return (
    <>
      <Row justify="space-between" align="middle" className='shopify-orders-list-header'>
        <Col>
          <Space direction='horizontal' size={4}>
            <Text strong style={{ fontSize: 14 }}>
              Total Line Items:
            </Text>
            {isLoading && pagination?.total === 0 ? (
              <Text style={{ color: '#999' }}>Loading...</Text>
            ) : (
              <Pagination
                size="small"
                current={pagination?.current || 1}
                total={pagination?.total || 0}
                pageSize={pagination?.pageSize || 10}
                onChange={handleSmallPaginationChange}
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} của ${total} line items`
                }
              />
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            {(canBulkEdit || canFulfillToPrinter) && (
              <>
                <Badge count={selectedRowKeys.length} offset={[-125, -5]} showZero={false}>
                  <Dropdown
                    menu={{
                      items: bulkActionMenuItems,
                      onClick: handleBulkActionMenuClick
                    }}
                    trigger={['hover']}
                    disabled={selectedRowKeys.length === 0}
                  >
                    <a style={{
                      color: selectedRowKeys.length === 0 ? '#d9d9d9' : undefined,
                      cursor: selectedRowKeys.length === 0 ? 'not-allowed' : 'pointer'
                    }}>
                      <ThunderboltOutlined /> Bulk Actions <CaretDownFilled />
                    </a>
                  </Dropdown>
                </Badge>
                {(canExportCSV || canSyncAmazon) && <Divider type="vertical" />}
              </>
            )}

            {canExportCSV && (
              <>
                <Dropdown
                  menu={{
                    items: dataMenuItems,
                    onClick: handleDataMenuClick
                  }}
                  trigger={['hover']}
                >
                  <a>
                    <DatabaseOutlined /> Data <CaretDownFilled />
                  </a>
                </Dropdown>
                {canSyncAmazon && <Divider type="vertical" />}
              </>
            )}

            {canSyncAmazon && (
              <Dropdown
                menu={{
                  items: syncMenuItems,
                  onClick: handleSyncMenuClick
                }}
                trigger={['hover']}
              >
                <a>
                  <SyncOutlined /> Sync <CaretDownFilled />
                </a>
              </Dropdown>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={processedData}
        loading={isLoading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="id"
        rowClassName={getRowClassName}
        scroll={{ x: 5030 }}
        sticky={{ offsetHeader: 75, offsetScroll: 32 }}
        rowSelection={rowSelection}
        virtual={false}
        showSorterTooltip={false}
        size="middle"
        bordered={false}
      />

      {/* Sync Orders Modal */}
      <SyncShopifyOrdersModal
        visible={syncModalVisible}
        onOk={handleSyncOrders}
        onCancel={() => setSyncModalVisible(false)}
        loading={syncLoading}
      />

      {/* Upload Shipping CSV Modal */}
      <UploadShippingCsvModal
        visible={uploadCsvModalVisible}
        onCancel={() => setUploadCsvModalVisible(false)}
        onUpload={handleUploadShippingCsv}
        loading={false}
      />

      {/* Shipping Info Modal */}
      <ShippingInfoModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        selectedOrder={selectedOrder}
      />

      {/* Customization Modal */}
      <ShopifyCustomizationModal
        visible={isCustomizationModalVisible}
        onCancel={() => setIsCustomizationModalVisible(false)}
        selectedCustomization={selectedCustomization}
      />

      {/* Shipping Timeline Modal */}
      <ShippingTimelineModal
        visible={isShippingTimelineModalVisible}
        onCancel={() => {
          setIsShippingTimelineModalVisible(false);
          setSelectedTrackingInfo(null);
        }}
        trackingInfo={selectedTrackingInfo}
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        visible={isBulkEditModalVisible && canBulkEdit}
        onCancel={() => setIsBulkEditModalVisible(false)}
        onConfirm={handleBulkEdit}
        selectedOrderItems={processedData.filter(item => selectedRowKeys.includes(item.id))}
        userOptions={userOptions}
        printerOptions={printerOptions}
        categoryOptions={categoryOptions}
        loading={isLoading}
      />

      <FulfillToPrinterModal
        visible={isFulfillToPrinterModalVisible && canFulfillToPrinter}
        platform="shopify"
        onCancel={() => {
          setIsFulfillToPrinterModalVisible(false);
          setSelectedOrdersForFulfill([]);
        }}
        onConfirm={async (responseData) => {
          if (!canFulfillToPrinter) {
            message.warning('Bạn không có quyền fulfill to printer');
            return;
          }

          message.success({
            content: `Fulfill to printer request submitted successfully! ${responseData.totalItems || 0} items are being processed.`,
            duration: 5
          });

          setIsFulfillToPrinterModalVisible(false);
          setSelectedOrdersForFulfill([]);

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }}
        selectedOrders={selectedOrdersForFulfill}
        loading={isLoading}
      />

      <ShopifyConfirmShipmentModal
        visible={isConfirmShipmentModalVisible && canConfirmShipment}
        onCancel={() => {
          setIsConfirmShipmentModalVisible(false);
          setShipmentReadyOrders([]);
        }}
        onConfirm={async (validOrders) => {
          if (!canConfirmShipment) {
            message.warning('Bạn không có quyền xác nhận shipment');
            return;
          }

          try {
            // Extract line item IDs from valid orders only
            const lineItemIds = validOrders.map(order => order.id);
            console.log('Confirming shipment for Shopify line item IDs:', lineItemIds);

            // Call confirmShopifyShipment action
            await dispatch(confirmShopifyShipment(lineItemIds, pagination));
          } catch (error) {
            console.error('Error confirming shipment:', error);
            // Error message already handled in action
          } finally {
            setIsConfirmShipmentModalVisible(false);
          }
        }}
        shipmentReadyOrders={shipmentReadyOrders}
        loading={isLoading}
      />

      {/* Send Mockup Modal */}
      <ShopifySendMockupModal
        visible={isSendMockupModalVisible && canSendMockup}
        onCancel={() => {
          setIsSendMockupModalVisible(false);
          setMockupReadyOrders([]);
        }}
        onConfirm={async () => {
          if (!canSendMockup) {
            message.warning('Bạn không có quyền gửi mockup');
            return;
          }

          try {
            const lineItemIds = mockupReadyOrders.map(order => order.id);
            console.log('Sending mockup for Shopify line item IDs:', lineItemIds);

            const response = await OrderService.sendShopifyMockup(lineItemIds);

            if (response.success) {
              message.success({
                content: `Mockup emails queued successfully! ${response.data.jobsCreated || 0} jobs created.`,
                duration: 5
              });

              setIsSendMockupModalVisible(false);
              setMockupReadyOrders([]);

              // Refresh orders list
              if (onPaginationChange) {
                onPaginationChange(pagination.current, pagination.pageSize);
              } else {
                dispatch(fetchOrderItems(pagination.current, pagination.pageSize));
              }
            } else {
              message.error(response.message || 'Failed to send mockup');
            }
          } catch (error) {
            console.error('Error sending mockup:', error);
            message.error('Failed to send mockup: ' + (error.message || 'Unknown error'));
          }
        }}
        mockupReadyOrders={mockupReadyOrders}
        loading={isLoading}
      />

      {/* Send Custom Message Modal */}
      <SendCustomMessageModal
        visible={isSendCustomMessageModalVisible && canSendCustomMessage}
        onCancel={() => {
          setIsSendCustomMessageModalVisible(false);
          setSelectedOrdersForCustomMessage([]);
        }}
        onConfirm={handleSendCustomMessage}
        selectedOrderItems={selectedOrdersForCustomMessage}
        loading={isLoading}
      />
    </>
  );
};

export default ShopifyOrdersList;
